// Spreadsheet import UI for the Administration page - 4 cards, one per
// import type, each opening a modal that goes through the two-phase
// preview/confirm flow (see api-client.ts's importApi and the backend
// services' routes/importRoutes.ts for the design). Reachable by ADMIN and
// SPONSOR-tier roles (canImportData()) even when the rest of the
// Administration page (users/sites/studies) is admin-only.
import { sitesApi, importApi, ApiError, type ImportPreview, type ImportFieldChange } from "./api-client.js";
import { escapeHtml, openModal, closeModal, notify, $, $$ } from "./dom.js";

interface ImportKind {
  key: "resultats" | "qol" | "ae" | "cm";
  icon: string;
  title: string;
  fileHint: string;
}

const IMPORT_KINDS: ImportKind[] = [
  { key: "resultats", icon: "🩺", title: "Résultats cliniques", fileHint: "Matrice résultats (vitaux, examen physique, caliper, bilan sanguin)" },
  { key: "qol", icon: "🐾", title: "Qualité de vie (CORQ)", fileHint: "Matrice qualité de vie (questionnaire CORQ)" },
  { key: "ae", icon: "⚠️", title: "Effets indésirables", fileHint: "Suivi des EI/EIG" },
  { key: "cm", icon: "💊", title: "Traitements concomittants", fileHint: "Suivi des traitements concomittants (ATCD + concomittants)" },
];

export async function renderImportSection(root: HTMLElement, preloadedSites: any[] | undefined): Promise<void> {
  root.innerHTML = `<div class="loading">Chargement…</div>`;
  const sites = preloadedSites ?? (await sitesApi.list());

  root.innerHTML = `
    <div class="section">
      <div class="section-header"><span class="title">📥 Import de données</span></div>
      <div class="section-body">
        <p class="field-hint">Importe un fichier Excel (matrice historique) pour un site investigateur donné. Chaque import affiche un aperçu détaillé avant toute écriture en base.</p>
        <div class="form-group" style="max-width:320px"><label>Site investigateur</label>
          <select id="importSiteSelect">${sites.map((s: any) => `<option value="${s.code}">${s.code} — ${escapeHtml(s.name)}</option>`).join("")}</select>
        </div>
        <div class="home-cards" id="importCards">
          ${IMPORT_KINDS.map(
            (k) => `<div class="home-card" data-import-kind="${k.key}">
              <div class="icon">${k.icon}</div>
              <h3>${escapeHtml(k.title)}</h3>
              <p>${escapeHtml(k.fileHint)}</p>
            </div>`
          ).join("")}
        </div>
      </div>
    </div>`;

  root.querySelectorAll<HTMLElement>("[data-import-kind]").forEach((card) => {
    card.addEventListener("click", () => {
      const kind = IMPORT_KINDS.find((k) => k.key === card.getAttribute("data-import-kind"))!;
      const siteCode = ($("#importSiteSelect") as HTMLSelectElement).value;
      openImportModal(kind, siteCode);
    });
  });
}

function openImportModal(kind: ImportKind, siteCode: string): void {
  openModal(
    `${kind.icon} Import — ${kind.title}`,
    `<div class="form-group"><label>Site investigateur</label><div><strong>${escapeHtml(siteCode)}</strong></div></div>
     <div class="form-group"><label>Fichier Excel (.xlsx)</label><input type="file" id="importFile" accept=".xlsx"></div>
     <div id="importPreviewArea"></div>`,
    `<button class="btn btn-secondary" id="importCancel">Annuler</button><button class="btn btn-primary" id="importAnalyze">Analyser</button>`,
    true
  );
  $("#importCancel")!.addEventListener("click", closeModal);
  $("#importAnalyze")!.addEventListener("click", () => void runPreview(kind, siteCode));
}

async function runPreview(kind: ImportKind, siteCode: string): Promise<void> {
  const input = $("#importFile") as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) { notify("error", "Sélectionnez un fichier."); return; }

  const analyzeBtn = $("#importAnalyze") as HTMLButtonElement;
  analyzeBtn.disabled = true;
  analyzeBtn.textContent = "Analyse en cours…";
  const area = $("#importPreviewArea")!;
  area.innerHTML = `<div class="loading">Analyse du fichier…</div>`;

  let preview: ImportPreview;
  try {
    preview = await importApi[kind.key].preview(siteCode, file);
  } catch (err) {
    const message = err instanceof Error ? err.message : "Erreur d'analyse";
    const details = err instanceof ApiError ? err.details : undefined;
    const detailsHtml = details?.length
      ? `<ul style="margin:8px 0 0;padding-left:18px">${details.map((d) => `<li>${escapeHtml(d)}</li>`).join("")}</ul>`
      : "";
    area.innerHTML = `<div class="error-box"><strong>${escapeHtml(message)}</strong>${detailsHtml}</div>`;
    analyzeBtn.disabled = false;
    analyzeBtn.textContent = "Analyser";
    return;
  }
  analyzeBtn.classList.add("hidden");
  renderPreview(kind, preview, area);
}

// Friendly labels for the known summary keys - falls back to the raw key
// for anything not listed here, so a new field added later still shows up.
const SUMMARY_LABELS: Record<string, string> = {
  patients: "Patients dans le fichier",
  patientsToCreate: "Nouveaux patients à créer",
  visits: "Visites dans le fichier",
  visitsNew: "Visites à créer",
  visitsChanged: "Visites à mettre à jour",
  visitsAlreadyUpToDate: "Visites déjà à jour (aucun changement)",
  rows: "Traitements dans le fichier",
  rowsNew: "Traitements à créer",
  rowsAlreadyImported: "Traitements déjà importés (ignorés)",
  events: "Événements dans le fichier",
  eventsNew: "Événements à créer",
  eventsAlreadyImported: "Événements déjà importés (ignorés)",
  atcdAlreadyImported: "Antécédents déjà importés (ignorés)",
  siteMismatches: "Patients d'un autre site (ignorés)",
  unmappedLabels: "Champs non reconnus (non importés)",
  notesNotImported: "Notes non importées (aucun champ correspondant)",
  studyExitNotes: "Patients avec une sortie d'étude signalée (à traiter manuellement)",
  warnings: "Valeurs non reconnues (valeur par défaut utilisée)",
};

// "Nothing new" = every count that represents actual writes is zero - the
// direct fix for a re-imported file that's already fully reflected in the
// database looking, at a glance, like it still has work to do.
const ZERO_MEANS_NOTHING_NEW = ["patientsToCreate", "visitsNew", "visitsChanged", "rowsNew", "eventsNew"];

const SECTION_LABELS: Record<string, string> = {
  vital: "Signes vitaux",
  physical: "Examen physique",
  caliper: "Mesure au pied à coulisse",
  blood: "Bilan sanguin",
  biopsy: "Diagnostic histologique",
  corq: "CORQ",
  _meta: "Visite",
};

// Machine field keys that aren't already self-explanatory (most section
// fields are, e.g. "TEMP" or "general" - these two are the sentinel keys
// diffResultatsVisits/diffCorqVisits use for the visit date and the CORQ
// overall score, which aren't a real section field).
const FIELD_LABELS: Record<string, string> = {
  visitDate: "Date de visite",
  eva_cm: "Score EVA (qualité de vie globale)",
};

function formatChangeValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return escapeHtml(JSON.stringify(v));
  return escapeHtml(String(v));
}

// Uniquely identifies one change for the approve/reject checkboxes and the
// commit-time filter below - patientId/visitKey/section/field never contain
// "|" (patient IDs, visit keys and machine field keys are all short
// alphanumeric-with-dashes tokens), so a plain join is a safe, simple key.
function changeKey(c: Pick<ImportFieldChange, "patientId" | "visitKey" | "section" | "field">): string {
  return `${c.patientId}|${c.visitKey}|${c.section}|${c.field}`;
}

// Groups the flat changes array into a patient -> visit -> section tree
// (same nesting a file explorer uses for folders), so a preview with many
// changes can be scanned/collapsed level by level instead of scrolling a
// long flat table where the patient/visit repeats on every row.
function groupChanges(changes: ImportFieldChange[]): Map<string, Map<string, Map<string, ImportFieldChange[]>>> {
  const tree = new Map<string, Map<string, Map<string, ImportFieldChange[]>>>();
  for (const c of changes) {
    const visits = tree.get(c.patientId) ?? tree.set(c.patientId, new Map()).get(c.patientId)!;
    const sections = visits.get(c.visitKey) ?? visits.set(c.visitKey, new Map()).get(c.visitKey)!;
    const fields = sections.get(c.section) ?? sections.set(c.section, []).get(c.section)!;
    fields.push(c);
  }
  return tree;
}

function renderChangeTree(changes: ImportFieldChange[]): string {
  const tree = groupChanges(changes);
  const patientNodes = [...tree.entries()].map(([patientId, visits]) => {
    const patientFieldCount = [...visits.values()].reduce((n, sections) => n + [...sections.values()].reduce((m, f) => m + f.length, 0), 0);
    const visitNodes = [...visits.entries()].map(([visitKey, sections]) => {
      const visitFieldCount = [...sections.values()].reduce((m, f) => m + f.length, 0);
      const sectionNodes = [...sections.entries()].map(([sectionId, fields]) => {
        const fieldRows = fields
          .map(
            (fc) => `<div class="tree-field-row">
              <label class="tree-field-approve"><input type="checkbox" class="change-approve" data-key="${escapeHtml(changeKey(fc))}" checked></label>
              <span class="tree-field-name">${escapeHtml(FIELD_LABELS[fc.field] ?? fc.field)}</span>
              <span class="tree-field-value tree-field-db">${formatChangeValue(fc.dbValue)}</span>
              <span class="tree-field-arrow">→</span>
              <span class="tree-field-value tree-field-file">${formatChangeValue(fc.fileValue)}</span>
            </div>`
          )
          .join("");
        return `<details class="tree-node tree-section">
          <summary>📄 ${escapeHtml(SECTION_LABELS[sectionId] ?? sectionId)} <span class="tree-count">${fields.length}</span></summary>
          <div class="tree-children">${fieldRows}</div>
        </details>`;
      }).join("");
      return `<details class="tree-node tree-visit">
        <summary>📁 ${escapeHtml(visitKey)} <span class="tree-count">${visitFieldCount}</span></summary>
        <div class="tree-children">${sectionNodes}</div>
      </details>`;
    }).join("");
    return `<details class="tree-node tree-patient">
      <summary>🧑 ${escapeHtml(patientId)} <span class="tree-count">${patientFieldCount}</span></summary>
      <div class="tree-children">${visitNodes}</div>
    </details>`;
  }).join("");
  return `<div class="change-tree">${patientNodes}</div>`;
}

// Strips any field the admin unchecked from the write plan before it's sent
// to commit - a field only ever appears in `rejected` if it was in
// preview.changes to begin with, so a brand-new patient/visit (never
// diffed, always fully written) and an already-up-to-date field (not worth
// gating, writing it again is a no-op) both pass through untouched. Only
// résultats/qol carry per-field changes at all (see diffResultatsVisits/
// diffCorqVisits) - cm/ae are dedup-only and always return unfiltered.
function filterRejectedChanges(kind: ImportKind["key"], operations: { siteCode: string; patients: any[] }, rejected: Set<string>): { siteCode: string; patients: any[] } {
  if (rejected.size === 0 || (kind !== "resultats" && kind !== "qol")) return operations;

  const isRejected = (patientId: string, visitKey: string, section: string, field: string) => rejected.has(`${patientId}|${visitKey}|${section}|${field}`);

  const patients = operations.patients.map((p: any) => ({
    ...p,
    visits: (p.visits ?? [])
      .map((v: any) => {
        const visit = { ...v };
        if (kind === "resultats") {
          if (isRejected(p.patientId, v.visitKey, "_meta", "visitDate")) visit.visitDate = null;
          const sections: Record<string, unknown> = {};
          for (const [sectionId, patch] of Object.entries(visit.sections ?? {})) {
            const kept = Object.fromEntries(
              Object.entries(patch as Record<string, unknown>).filter(([field]) => !isRejected(p.patientId, v.visitKey, sectionId, field))
            );
            if (Object.keys(kept).length > 0) sections[sectionId] = kept;
          }
          visit.sections = sections;
        } else {
          if (isRejected(p.patientId, v.visitKey, "corq", "eva_cm")) visit.evaCm = null;
          visit.items = Object.fromEntries(
            Object.entries(visit.items ?? {}).filter(([field]) => !isRejected(p.patientId, v.visitKey, "corq", field))
          );
        }
        return visit;
      })
      // A visit that ended up with nothing left to write (every one of its
      // changes was unchecked) is dropped entirely rather than sent as an
      // empty no-op write.
      .filter((v: any) =>
        kind === "resultats"
          ? v.visitDate || Object.keys(v.sections).length > 0
          : v.evaCm !== null || Object.keys(v.items).length > 0
      ),
  }));
  return { ...operations, patients };
}

function renderPreview(kind: ImportKind, preview: ImportPreview, area: HTMLElement): void {
  const summaryRows = Object.entries(preview.summary)
    .map(([k, v]) => `<tr><td>${escapeHtml(SUMMARY_LABELS[k] ?? k)}</td><td>${escapeHtml(Array.isArray(v) ? (v.length ? v.join(", ") : "—") : String(v))}</td></tr>`)
    .join("");

  const patientRows = preview.operations.patients
    .map((p: any) => {
      const exists = preview.patientsExist[p.patientId];
      return `<tr>
        <td>${escapeHtml(p.patientId)}</td>
        <td>${escapeHtml(p.initials)}</td>
        <td>${exists ? '<span class="badge badge-info">Existant</span>' : '<span class="badge badge-warning">Nouveau patient</span>'}</td>
      </tr>`;
    })
    .join("");

  const relevantZeroChecks = ZERO_MEANS_NOTHING_NEW.filter((k) => k in preview.summary);
  const nothingNew = relevantZeroChecks.length > 0 && relevantZeroChecks.every((k) => Number(preview.summary[k]) === 0);

  const changes = preview.changes ?? [];
  const changesHtml = changes.length
    ? `<h4 style="margin-top:14px">Modifications détectées (${changes.length})</h4>
       <p class="field-hint">Patient → Visite → Section. Décochez une modification pour l'ignorer lors de l'import - seules les modifications cochées seront écrites en base.</p>
       <div style="margin-bottom:8px"><button type="button" class="btn btn-sm btn-secondary" id="changesSelectAll">Tout cocher</button> <button type="button" class="btn btn-sm btn-secondary" id="changesSelectNone">Tout décocher</button></div>
       ${renderChangeTree(changes)}`
    : "";

  area.innerHTML = `
    ${nothingNew ? `<div class="success-box">Ce fichier est déjà entièrement reflété en base - aucune nouvelle donnée à importer.</div>` : ""}
    <h4 style="margin-top:14px">Résumé</h4>
    <table class="data-table"><tbody>${summaryRows}</tbody></table>
    ${preview.siteMismatches.length ? `<div class="alert-pv" style="margin-top:10px"><strong>${preview.siteMismatches.length} patient(s) ignoré(s)</strong> (site différent du site sélectionné) : ${escapeHtml(preview.siteMismatches.join(", "))}</div>` : ""}
    <h4 style="margin-top:14px">Patients (${preview.operations.patients.length})</h4>
    <table class="data-table"><thead><tr><th>ID</th><th>Initiales</th><th>Statut</th></tr></thead><tbody>${patientRows || `<tr><td colspan="3" class="muted">Aucune donnée exploitable dans ce fichier pour ce site.</td></tr>`}</tbody></table>
    ${changesHtml}
    <p class="field-hint" style="margin-top:10px">Cliquez sur « Confirmer l'import » pour écrire ces données en base. Les patients marqués « Nouveau patient » seront créés automatiquement.</p>`;

  $("#changesSelectAll")?.addEventListener("click", () => { $$<HTMLInputElement>(".change-approve").forEach((cb) => (cb.checked = true)); });
  $("#changesSelectNone")?.addEventListener("click", () => { $$<HTMLInputElement>(".change-approve").forEach((cb) => (cb.checked = false)); });

  const footer = $("#modalFooter")!;
  footer.innerHTML = `<button class="btn btn-secondary" id="importCancel2">Annuler</button><button class="btn btn-primary" id="importConfirm" ${preview.operations.patients.length === 0 ? "disabled" : ""}>${nothingNew ? "Confirmer quand même" : "Confirmer l'import"}</button>`;
  $("#importCancel2")!.addEventListener("click", closeModal);
  $("#importConfirm")!.addEventListener("click", async () => {
    const btn = $("#importConfirm") as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = "Import en cours…";
    try {
      const rejected = new Set($$<HTMLInputElement>(".change-approve:not(:checked)").map((cb) => cb.dataset.key!));
      const operations = filterRejectedChanges(kind.key, preview.operations, rejected);
      const result = await importApi[kind.key].commit(operations);
      const { errors, ...counts } = result;
      const countsHtml = Object.entries(counts).map(([k, v]) => `<li>${escapeHtml(k)} : ${escapeHtml(String(v))}</li>`).join("");
      const errorsHtml = errors.length
        ? `<div class="alert-sae" style="margin-top:10px"><strong>${errors.length} erreur(s)</strong><ul>${errors.map((e) => `<li>${escapeHtml(e)}</li>`).join("")}</ul></div>`
        : "";
      area.innerHTML = `<div class="success-box">Import terminé.<ul>${countsHtml}</ul></div>${errorsHtml}`;
      footer.innerHTML = `<button class="btn btn-primary" id="importDone">Fermer</button>`;
      $("#importDone")!.addEventListener("click", closeModal);
    } catch (err) {
      notify("error", err instanceof Error ? err.message : "Erreur lors de l'import");
      btn.disabled = false;
      btn.textContent = "Confirmer l'import";
    }
  });
}
